/**
 * @file: provider.tsx
 *
 * `ChatConversationProvider` — owns the SHARED state of a single open conversation,
 * exposed via `useChatConversation()`. Thin wiring of focused hooks around the
 * `coordinator` event bus.
 *
 * The graph is acyclic because the parts never reference each other — they
 * talk through the coordinator's intents. The provider owns only the single
 * source of truth (`messages$`); it creates the `coordinator` first (it
 * depends on nothing in the domain), then hands it to the store + pipeline,
 * which subscribe to / emit on its bus. Every backing hook reads the app-wide
 * session / inbox store it needs DIRECTLY — nothing is threaded through here.
 * Mounted by `ChatConversationView` (the mobile screen + desktop pane both
 * render it).
 */

import { ChatMsg } from '@euler/model/chat';
import { useCreation } from '@euler/utils/hooks';
import { FC, ReactNode, memo, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';
import {
  ChatConversationContext,
  ChatConversationContextValue,
} from './context';
import { BakedConversationHandle, ChatConversationHandle } from './handle';
import { useChatSendPipeline } from './hooks/useChatSendPipeline';
import { useConversationCoordinator } from './hooks/useConversationCoordinator';
import { useConversationHandle } from './hooks/useConversationHandle';
import { useConversationMembers } from './hooks/useConversationMembers';
import { useConversationPresence } from './hooks/useConversationPresence';
import { useMessageList } from './hooks/useMessageList';

export const ChatConversationProvider: FC<{
  conversationHandle: ChatConversationHandle;
  /** Host callback: report the baked handle once a first send materialises
   * the room/thread (mobile → route params, desktop → its selection). */
  onConversationHandleMaterialized?: (handle: BakedConversationHandle) => void;
  children: ReactNode;
}> = memo(
  ({
    conversationHandle: resolvedHandle,
    onConversationHandleMaterialized,
    children,
  }) => {
    const { conversationHandle, materializeHandle } = useConversationHandle(
      resolvedHandle,
      onConversationHandleMaterialized,
    );

    // Single source of truth for the message list — the store mutates it, the
    // view reads it. (The reply target lives on the coordinator.)
    const messages$ = useCreation(() => new BehaviorSubject<ChatMsg[]>([]), []);

    // The event bus — created first; the store + pipeline receive it and
    // subscribe / emit. It also materialises the room handle off `messageSent$`
    // (the first send creates the room/thread), so it takes the handle + setter.
    const coordinator = useConversationCoordinator({
      messages$,
      conversationHandle,
      onConversationHandleMaterialized: materializeHandle,
    });

    // Self-contained: members refresh on `members.invited`, the store listens
    // for message-level events — no central event router here.
    const members$ = useConversationMembers(conversationHandle);

    const list = useMessageList({ conversationHandle, messages$, coordinator });

    const pipeline = useChatSendPipeline({ conversationHandle, coordinator });

    useConversationPresence(conversationHandle);

    const value = useMemo<ChatConversationContextValue>(
      () => ({
        conversationHandle,

        messages$,
        loading$: list.loading$,
        loadingOlder$: list.loadingOlder$,
        loadOlder: list.loadOlder,

        sendPayload: pipeline.sendPayload,
        sendMediaAssets: pipeline.sendMediaAssets,
        sendMediaSeparately: pipeline.sendMediaSeparately,
        sendFile: pipeline.sendFile,
        sendAudio: pipeline.sendAudio,

        members$,

        coordinator,
      }),
      [
        conversationHandle,
        messages$,
        list.loading$,
        list.loadingOlder$,
        list.loadOlder,
        pipeline.sendPayload,
        pipeline.sendMediaAssets,
        pipeline.sendMediaSeparately,
        pipeline.sendFile,
        pipeline.sendAudio,
        members$,
        coordinator,
      ],
    );

    return (
      <ChatConversationContext.Provider value={value}>
        {children}
      </ChatConversationContext.Provider>
    );
  },
);
